/* eslint-disable jsx-a11y/anchor-is-valid */

import React, { FC, useState, useRef, useEffect } from 'react';
import { Button as DSButton, Card, Dialog, Field, Heading, Input, Label, Paragraph, Tag, ValidationMessage } from '@udir-design/react/beta';
import { PadlockLockedFillIcon, PadlockUnlockedFillIcon } from '@udir-design/icons';
import { getContentType, getExamPeriod } from '../../../utils/resultHelpers';
import { getLanguage } from '../../../utils/searchHelpers';
import { DisplayFormat } from '../../../Models/DisplayFormat';
import { getCurrentDisplayFormat } from '../../../utils/generalHelpers';
import getEksamenFile, { EksamensFileQuery, EksamenFileStatus } from '../../../utils/eksamensHelpers';

export enum ModalMode {
    Off,
    Regular,
    Forbredelse,
    Vedlegg,
    VedleggForberedelse
}

export interface IEksamensResultCardProps {
    result: {
        title: string;
        Url?: string;
        udirtitle?: string;
        eksamenstype?: string;
        innholdstype?: string;
        periode?: string;
        ExPeriodName?: string;
        katalogtype?: string;
        ExCatalogTypeName?: string;
        passordbeskyttet?: string;
        passordbeskyttetforberedelse?: string;
        passordbeskyttetvedleggtileksamen?: string;
        passordbeskyttetvedleggtilforberedelse?: string;
        passord?: string;
        ExDocumentRepositoryCode?: string;
        urldoklager?: string;
        ExListID?: string;
        ExListItemID?: string;
        ExSPSiteURL?: string;
        ExUIVersionStringOWSTEXT?: string;
        ExUniqueID?: string;
        ExListItemIDForb?: string;
        ExUniqueIDForb?: string;
        ExListItemIDVedlegg?: string;
        ExListItemIDVedleggForb?: string;
        ExUniqueIDVedlegg?: string;
        ExUniqueIDVedleggForb?: string;
        ExFilenameVedlegg?: string;
        ExFilenameVedleggForb?: string;
        filnavn?: string;
        filnavnforberedelse?: string;
        fileextension?: string;
        ExKL?: string;
        maalform?: string;
        ExMaalform?: string;
        fagkode?: string;
        language?: string;
    };
    idx: number;
    otherIsDownloading?: boolean;
    setDownloading?: (downloading: boolean) => void;
}

const titleWithTilpassing = (result: IEksamensResultCardProps['result']): string => {
    const title = result.title;
    const katalogtype = result.katalogtype || result.ExCatalogTypeName;
    if (katalogtype?.includes("Tilpasset hørselshemmede")) {
        return title + " tilpasset hørselshemmede";
    } else if (katalogtype?.includes("Tilpasset blinde og svaksynte")) {
        return title + " tilpasset blinde og svaksynte";
    } else if (katalogtype?.includes("Lydfil med innlest oppgave")) {
        return title + " lydfil med innlest oppgave";
    }
    return title;
};

const isDownloadingStatus = (status: EksamenFileStatus): boolean => {
    if (status === undefined
        || status === EksamenFileStatus.NoAction
        || status === EksamenFileStatus.Downloaded
        || status === EksamenFileStatus.DownloadError
        || status === EksamenFileStatus.ServerError) {
        return false;
    }
    return true;
};

const startDownload = (result: IEksamensResultCardProps['result'], updateFileStatus, displayMode: ModalMode, password: string, setIsDownloading: (v: boolean) => void) => {
    setIsDownloading(true);
    const r = result as any;

    let eksamenFileQuery: EksamensFileQuery;
    if (displayMode === ModalMode.Regular) {
        eksamenFileQuery = {
            ExIsPasswordProtected: r.passordbeskyttet,
            ExListID: r.ExListID,
            ExListItemID: r.ExListItemID,
            ExSPSiteURL: r.ExSPSiteURL,
            ExUIVersionStringOWSTEXT: r.ExUIVersionStringOWSTEXT,
            modalpassword: password,
            ExPassord: r.passord,
            filnavn: r.filnavn,
            updateStatus: updateFileStatus,
            DocumentGuid: r.ExUniqueID,
            innholdstype: r.eksamenstype
        }
    } else if (displayMode === ModalMode.Vedlegg) {
        eksamenFileQuery = {
            ExIsPasswordProtected: r.passordbeskyttetvedleggtileksamen,
            ExListID: r.ExListID,
            ExListItemID: r.ExListItemIDVedlegg,
            ExSPSiteURL: r.ExSPSiteURL,
            ExUIVersionStringOWSTEXT: r.ExUIVersionStringOWSTEXT,
            modalpassword: password,
            ExPassord: r.passord,
            filnavn: r.ExFilenameVedlegg,
            updateStatus: updateFileStatus,
            DocumentGuid: r.ExUniqueIDVedlegg,
            innholdstype: r.eksamenstype
        }
    } else if (displayMode === ModalMode.VedleggForberedelse) {
        eksamenFileQuery = {
            ExIsPasswordProtected: r.passordbeskyttetvedleggtilforberedelse,
            ExListID: r.ExListID,
            ExListItemID: r.ExListItemIDVedleggForb,
            ExSPSiteURL: r.ExSPSiteURL,
            ExUIVersionStringOWSTEXT: r.ExUIVersionStringOWSTEXT,
            modalpassword: password,
            ExPassord: r.passord,
            filnavn: r.ExFilenameVedleggForb,
            updateStatus: updateFileStatus,
            DocumentGuid: r.ExUniqueIDVedleggForb,
            innholdstype: r.eksamenstype
        }
    } else {
        eksamenFileQuery = {
            ExIsPasswordProtected: r.passordbeskyttetforberedelse,
            ExListID: r.ExListID,
            ExListItemID: r.ExListItemIDForb,
            ExSPSiteURL: r.ExSPSiteURL,
            ExUIVersionStringOWSTEXT: r.ExUIVersionStringOWSTEXT,
            modalpassword: password,
            ExPassord: r.passord,
            filnavn: r.filnavnforberedelse,
            updateStatus: updateFileStatus,
            DocumentGuid: r.ExUniqueIDForb,
            innholdstype: r.eksamenstype
        }
    }
    getEksamenFile(eksamenFileQuery);
};

const EksamensResultCard: FC<IEksamensResultCardProps> = (props) => {
    const { result, otherIsDownloading = false, setDownloading: notifyDownloading } = props;
    const [displayModal, setDisplayModal] = useState(ModalMode.Off);
    const [fileStatus, setFileStatus] = useState(EksamenFileStatus.NoAction);
    const [passwordInput, setPasswordInput] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const passwordInputRef = useRef<HTMLInputElement>(null);
    const isMobile = getCurrentDisplayFormat() === DisplayFormat.Mobile;

    const updateFileStatus = (status: EksamenFileStatus) => {
        setFileStatus(status);
        if (status === EksamenFileStatus.WrongPassword || status === EksamenFileStatus.ServerError || status === EksamenFileStatus.DownloadError || status === EksamenFileStatus.Downloaded) {
            setIsSubmitting(false);
        }
    };

    const getStoredPassword = (): string | null => {
        if (result.eksamenstype === "Sensorskolering") {
            return sessionStorage.getItem('udirsokeksamensessionpassword-blank');
        } else {
            return sessionStorage.getItem('udirsokeksamensessionpassword');
        }
    };
    const storedPassword = getStoredPassword();

    const handleHeaderClick = () => {
        const currentPassword = getStoredPassword();
        if (result.passordbeskyttet === 'true' && !currentPassword) {
            setDisplayModal(ModalMode.Regular);
        } else {
            startDownload(result, updateFileStatus, ModalMode.Regular, currentPassword || '', notifyDownloading || (() => {}));
        }
    };

    const isPGSD = result.ExDocumentRepositoryCode != null;
    const downloading = isDownloadingStatus(fileStatus);
    const onSubmitPassword = (password: string) => {
        // Vis "Laster ned..."-tilbakemelding umiddelbart, før vi bygger query/fetch
        setIsSubmitting(true);
        startDownload(result, updateFileStatus, displayModal, password, notifyDownloading || (() => {}));
    };
    const isDownloadingClass = downloading || displayModal !== ModalMode.Off ? 'isDownloading' : '';
    const disabledLinkClass = otherIsDownloading ? 'isDisabled' : '';

    // Cross-card download state notifications
    if (notifyDownloading) {
        if (fileStatus === EksamenFileStatus.Downloaded) {
            notifyDownloading(false);
        }
        if (fileStatus === EksamenFileStatus.DownloadError) {
            notifyDownloading(false);
        }
        if (displayModal) {
            notifyDownloading(true);
        }
    }

    useEffect(() => {
        if (displayModal !== ModalMode.Off && passwordInputRef.current) {
            const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
            if (!isMobileDevice) {
                setTimeout(() => {
                    passwordInputRef.current?.focus();
                }, 100);
            }
        }
    }, [displayModal]);

    useEffect(() => {
        if (fileStatus === EksamenFileStatus.Downloaded) {
            const timer = setTimeout(() => {
                setDisplayModal(ModalMode.Off);
                if (notifyDownloading) notifyDownloading(false);
                setFileStatus(EksamenFileStatus.NoAction);
                setPasswordInput('');
                setIsSubmitting(false);
            }, 1500);
            return () => clearTimeout(timer);
        }
    }, [fileStatus, notifyDownloading]);

    const displayTitle = titleWithTilpassing(result);
    const examPeriod = getExamPeriod(result) || result.ExPeriodName;
    const maalform = result.maalform || result.ExMaalform;
    const language_attr = getLanguage(result);

    return (
        <Card className={`ResultLinkCard ${isDownloadingClass}`} key={`resultDisplay-${props.idx}`}>
            <Card.Block className="LinkCard__container">
                <div className={`LinkCard ${isDownloadingClass}`}>
                    <Paragraph className="LinkCard__headerandsubtitle" data-size="sm">
                        <span className="subtitle-top title">{getContentType(result)}</span>
                        {examPeriod && (
                            <span className="subtitle-top date">{examPeriod}</span>
                        )}
                    </Paragraph>

                    {isPGSD ? (
                        <Heading level={2} className="headerandbuttons" {...language_attr}>
                            <a
                                className={`cardtitle ${disabledLinkClass}`}
                                href="#"
                                onClick={!otherIsDownloading ? (e) => { e.preventDefault(); handleHeaderClick(); } : undefined}
                            >
                                {displayTitle}
                                {result.passordbeskyttet === "true" && (
                                    storedPassword ? (
                                        <PadlockUnlockedFillIcon
                                            className="exam-locked-icon"
                                            title="Passordet er lagret i denne økten. Oppgavene er passordbeskyttet på grunn av opphavsrett. Det er ikke meningen at oppgavene skal være hemmelige for elevene. Skoler og privatistkontor har fått tilsendt passord."
                                        />
                                    ) : (
                                        <PadlockLockedFillIcon
                                            className="exam-locked-icon"
                                            title="Oppgavene er passordbeskyttet på grunn av opphavsrett. Det er ikke meningen at oppgavene skal være hemmelige for elevene. Skoler og privatistkontor har fått tilsendt passord."
                                        />
                                    )
                                )}
                            </a>
                        </Heading>
                    ) : (
                        <Heading level={2} className="headerandbuttons" {...language_attr}>
                            <a className="cardtitle" href={result.Url} rel="noopener noreferrer">
                                {displayTitle}
                            </a>
                        </Heading>
                    )}

                    <div className="TagGroup metadata" data-size={isMobile ? 'sm' : 'md'}>
                        {result.ExKL && result.ExKL.trim().length > 0 && (
                            <Tag data-color={result.ExKL.toLowerCase() === "kunnskapsløftet 2020" ? "accent" : "warning"}>{result.ExKL}</Tag>
                        )}
                        {maalform && maalform.trim().length > 0 && (
                            <Tag data-color="accent">{maalform}</Tag>
                        )}
                        {result.fagkode && result.fagkode.trim().length > 0 && (
                            <Tag data-color="accent">{result.fagkode}</Tag>
                        )}
                    </div>

                    <Dialog
                        open={displayModal !== ModalMode.Off}
                        closedby="any"
                        onClose={() => {
                            setDisplayModal(ModalMode.Off);
                            if (notifyDownloading) notifyDownloading(false);
                            setFileStatus(EksamenFileStatus.NoAction);
                            setPasswordInput('');
                            setIsSubmitting(false);
                        }}
                    >
                        <Dialog.Block>
                            <Heading level={2}>Last ned eksamensoppgave ({result.fileextension})</Heading>
                            
                            <p className="exam-modal-info" style={{ marginTop: 'var(--ds-size-4)' }}>
                                Oppgavene er passordbeskyttet på grunn av opphavsrett. Det er ikke meningen at oppgavene skal være hemmelige for elevene. Skoler og privatistkontor har fått tilsendt passord.
                            </p>

                            <Field className='exam-modal-field'>
                                <Label>Skriv passord for å laste ned.</Label>
                                <Input
                                    ref={passwordInputRef}
                                    className="exam-modal-input"
                                    placeholder='Passord'
                                    type='password'
                                    value={passwordInput}
                                    onChange={(e) => setPasswordInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            onSubmitPassword(passwordInput);
                                        }
                                    }}
                                    autoComplete="off"
                                />
                                {fileStatus === EksamenFileStatus.WrongPassword && (
                                    <ValidationMessage className='exam-modal-validationmsg'>Passordet er feil. Prøv på nytt.</ValidationMessage>
                                )}
                                {(isSubmitting || fileStatus === EksamenFileStatus.CorrectPassword || (fileStatus > EksamenFileStatus.CorrectPassword && fileStatus < EksamenFileStatus.Downloaded && fileStatus !== EksamenFileStatus.WrongPassword)) && fileStatus !== EksamenFileStatus.WrongPassword && (
                                    <ValidationMessage className='exam-modal-validationmsg' data-color='info'>Laster ned...</ValidationMessage>
                                )}
                                {fileStatus === EksamenFileStatus.Downloaded && (
                                    <ValidationMessage className='exam-modal-validationmsg' data-color='success'>Nedlasting fullført!</ValidationMessage>
                                )}
                            </Field>

                            {(fileStatus === EksamenFileStatus.ServerError || fileStatus === EksamenFileStatus.DownloadError) && (
                                <p style={{ color: 'var(--ds-color-text-danger)', marginTop: 'var(--ds-size-2)' }}>
                                    Noe gikk galt. Vennligst prøv igjen senere.
                                </p>
                            )}

                            <div className='exam-download-btn'>
                                <DSButton
                                    loading={isSubmitting}
                                    disabled={isSubmitting || fileStatus === EksamenFileStatus.Downloaded}
                                    variant="primary"
                                    onClick={() => {
                                        onSubmitPassword(passwordInput);
                                    }}
                                >
                                    Last ned
                                </DSButton>
                            </div>
                        </Dialog.Block>
                    </Dialog>
                </div>
            </Card.Block>
        </Card>
    );
};

export default EksamensResultCard;
